export const APPLY_DRAFT_PLACEHOLDER_TEXT = "已生成可确认的表单草稿，请在下方卡片核对后应用。";

export function parseJsonContent(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed || (!trimmed.startsWith("{") && !trimmed.startsWith("["))) {
    return null;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

export function extractJsonObjects(text) {
  const source = String(text || "");
  const results = [];
  const starts = [];

  for (let i = 0; i < source.length; i += 1) {
    if (source[i] === "{") {
      starts.push(i);
    }
  }

  starts.forEach((start) => {
    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let i = start; i < source.length; i += 1) {
      const char = source[i];

      if (inString) {
        if (escaped) {
          escaped = false;
        } else if (char === "\\") {
          escaped = true;
        } else if (char === "\"") {
          inString = false;
        }
        continue;
      }

      if (char === "\"") {
        inString = true;
      } else if (char === "{") {
        depth += 1;
      } else if (char === "}") {
        depth -= 1;
        if (depth === 0) {
          const candidate = source.slice(start, i + 1);
          try {
            results.push(JSON.parse(candidate));
          } catch {
            // Ignore non-JSON braces in model output.
          }
          break;
        }
      }
    }
  });

  return results;
}

export function getApplyDraftFromText(text) {
  const directJson = parseJsonContent(text);
  const candidates = directJson !== null ? [directJson] : extractJsonObjects(text);
  return candidates.find((item) => item?.type === "kmauth.applyDraft" && item?.entity && item?.fields && typeof item.fields === "object") || null;
}

function getApplyDraftJsonRanges(text) {
  const source = String(text || "");
  const ranges = [];
  const starts = [];

  for (let i = 0; i < source.length; i += 1) {
    if (source[i] === "{") {
      starts.push(i);
    }
  }

  starts.forEach((start) => {
    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let i = start; i < source.length; i += 1) {
      const char = source[i];

      if (inString) {
        if (escaped) {
          escaped = false;
        } else if (char === "\\") {
          escaped = true;
        } else if (char === "\"") {
          inString = false;
        }
        continue;
      }

      if (char === "\"") {
        inString = true;
      } else if (char === "{") {
        depth += 1;
      } else if (char === "}") {
        depth -= 1;
        if (depth === 0) {
          const candidate = source.slice(start, i + 1);
          try {
            const parsed = JSON.parse(candidate);
            if (parsed?.type === "kmauth.applyDraft") {
              ranges.push({start, end: i + 1});
            }
          } catch {
            // Ignore non-JSON braces in model output.
          }
          break;
        }
      }
    }
  });

  return ranges;
}

export function getSafeDraftMessageText(text) {
  const source = String(text || "");
  const ranges = getApplyDraftJsonRanges(source);
  if (ranges.length === 0) {
    return source;
  }

  let result = "";
  let lastIndex = 0;

  ranges
    .sort((a, b) => a.start - b.start)
    .forEach((range) => {
      if (range.start < lastIndex) {
        return;
      }

      result += source.slice(lastIndex, range.start);
      if (result && !result.endsWith("\n")) {
        result += "\n";
      }
      result += APPLY_DRAFT_PLACEHOLDER_TEXT;
      if (source[range.end] && source[range.end] !== "\n") {
        result += "\n";
      }
      lastIndex = range.end;
    });

  result += source.slice(lastIndex);
  return result;
}

export function getDraftEntityLabel(entity) {
  return {
    organization: "组织",
    user: "用户",
    application: "应用",
    provider: "Provider",
  }[entity] || entity || "对象";
}

export function getDraftTargetSummary(draft) {
  const target = draft?.target || {};
  const fields = draft?.fields || {};

  if (draft?.entity === "application") {
    const organization = target.organization || fields.organization || target.owner || fields.owner || "";
    const name = target.name || fields.name || "";
    return [organization, name].filter(Boolean).join("/");
  }

  const owner = target.owner || fields.owner || "";
  const name = target.name || fields.name || "";
  return [owner, name].filter(Boolean).join("/") || "未指定目标";
}

export function getDraftFieldValue(value) {
  if (value === null || value === undefined || value === "") {
    return "空";
  }
  if (Array.isArray(value)) {
    return value.map((item) => typeof item === "object" ? JSON.stringify(item) : String(item)).join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return String(value);
}

export function isSensitiveDraftField(key) {
  const fieldKey = String(key || "");
  if (["tokenFormat", "tokenFields", "tokenAttributes"].includes(fieldKey)) {
    return false;
  }
  if (/url$/i.test(fieldKey) || /endpoint/i.test(fieldKey)) {
    return false;
  }
  return /(secret|password|token|apiKey|accessKey|privateKey|keyText|credential)/i.test(fieldKey);
}

export function isSensitiveDraftTextValue(value) {
  return typeof value === "string" && /^(bearer|basic)\s+/i.test(value.trim());
}

export function shouldMaskDraftValue(key, value) {
  return value !== null && value !== undefined && value !== "" && (isSensitiveDraftField(key) || isSensitiveDraftTextValue(value));
}

export function maskDraftValue(key, value) {
  if (shouldMaskDraftValue(key, value)) {
    return "••••••";
  }

  if (Array.isArray(value)) {
    return value.map((item) => maskDraftValue("", item));
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((result, [nestedKey, nestedValue]) => ({
      ...result,
      [nestedKey]: maskDraftValue(nestedKey, nestedValue),
    }), {});
  }

  return value;
}

const DRAFT_HISTORY_STORAGE_KEY = "kmauth.aiDraftHistory.v1";
const DRAFT_HISTORY_LIMIT = 50;

export const DRAFT_SCHEMAS = {
  application: {
    required: {
      add: ["fields.owner", "fields.organization", "fields.name"],
      edit: ["target.organization", "target.name"],
    },
    fields: {
      owner: {group: "identity", type: "string", requiredOnAdd: true},
      organization: {group: "identity", type: "string", requiredOnAdd: true},
      name: {group: "identity", type: "string", requiredOnAdd: true},
      displayName: {group: "identity", type: "string"},
      homepageUrl: {group: "endpoint", type: "url"},
      redirectUris: {group: "endpoint", type: "url[]"},
      signupUrl: {group: "endpoint", type: "url"},
      signinUrl: {group: "endpoint", type: "url"},
      forcedRedirectOrigin: {group: "endpoint", type: "url"},
      clientId: {group: "credential", type: "string"},
      clientSecret: {group: "credential", type: "string", sensitive: true},
      grantTypes: {group: "authorization", type: "oauthGrant[]"},
      tokenFormat: {group: "authorization", type: "string"},
      tokenFields: {group: "authorization", type: "string[]"},
      expireInHours: {group: "authorization", type: "number"},
      refreshExpireInHours: {group: "authorization", type: "number"},
      cookieExpireInHours: {group: "authorization", type: "number"},
      enablePkce: {group: "advanced", type: "boolean"},
      enableSignUp: {group: "advanced", type: "boolean"},
      disableSignin: {group: "advanced", type: "boolean", risk: "high"},
      enableExclusiveSignin: {group: "advanced", type: "boolean", risk: "high"},
    },
  },
  provider: {
    required: {
      add: ["fields.owner", "fields.name", "fields.category", "fields.type"],
      edit: ["target.owner", "target.name"],
    },
    fields: {
      owner: {group: "identity", type: "string", requiredOnAdd: true},
      name: {group: "identity", type: "string", requiredOnAdd: true},
      displayName: {group: "identity", type: "string"},
      category: {group: "identity", type: "string", requiredOnAdd: true},
      type: {group: "identity", type: "string", requiredOnAdd: true},
      clientId: {group: "credential", type: "string"},
      clientSecret: {group: "credential", type: "string", sensitive: true},
      clientSecret2: {group: "credential", type: "string", sensitive: true},
      accessKey: {group: "credential", type: "string", sensitive: true},
      secretKey: {group: "credential", type: "string", sensitive: true},
      privateKey: {group: "credential", type: "string", sensitive: true},
      providerUrl: {group: "endpoint", type: "url"},
      customAuthUrl: {group: "endpoint", type: "url"},
      customTokenUrl: {group: "endpoint", type: "url"},
      customUserInfoUrl: {group: "endpoint", type: "url"},
      scopes: {group: "endpoint", type: "string"},
      host: {group: "endpoint", type: "string"},
      port: {group: "endpoint", type: "number"},
      title: {group: "template", type: "string"},
      content: {group: "template", type: "string"},
      metadata: {group: "template", type: "string"},
      receiver: {group: "template", type: "string"},
    },
  },
};

export const DRAFT_TEMPLATES = {
  application: [
    {
      id: "web-authorization-code",
      label: "Web 服务端应用",
      description: "适用于有后端服务的 OAuth/OIDC Authorization Code 接入。",
      fields: {
        grantTypes: ["authorization_code", "refresh_token"],
        tokenFormat: "JWT",
        enablePkce: false,
      },
    },
    {
      id: "spa-pkce",
      label: "SPA/移动端 PKCE",
      description: "适用于浏览器单页应用或移动端，默认开启 PKCE。",
      fields: {
        grantTypes: ["authorization_code", "refresh_token"],
        tokenFormat: "JWT",
        enablePkce: true,
      },
    },
    {
      id: "machine-client-credentials",
      label: "机器到机器",
      description: "适用于服务间调用的 Client Credentials 接入。",
      fields: {
        grantTypes: ["client_credentials"],
        tokenFormat: "JWT",
        enablePkce: false,
      },
    },
  ],
  provider: [
    {
      id: "custom-oidc-provider",
      label: "自定义 OIDC Provider",
      description: "适用于企业 IdP 的 OAuth/OIDC 对接。",
      fields: {
        category: "OAuth",
        type: "Custom",
        scopes: "openid profile email",
      },
    },
    {
      id: "smtp-email-provider",
      label: "SMTP 邮件 Provider",
      description: "适用于验证码、通知邮件发送配置。",
      fields: {
        category: "Email",
        type: "Default",
      },
    },
  ],
};

export function getDraftSchema(entity) {
  return DRAFT_SCHEMAS[entity] || {required: {add: [], edit: []}, fields: {}};
}

export function getDraftTemplates(entity) {
  return DRAFT_TEMPLATES[entity] || [];
}

function getDraftRiskLabel(level) {
  return {
    high: "高风险",
    medium: "需核对",
    low: "低风险",
  }[level] || "需核对";
}

function getRiskLevel(items) {
  if (items.some((item) => item.level === "high")) {
    return "high";
  }
  if (items.some((item) => item.level === "medium")) {
    return "medium";
  }
  return "low";
}

function hasValueForRisk(value) {
  return value !== null && value !== undefined && value !== "" && (!Array.isArray(value) || value.length > 0);
}

export function getDraftRiskSummary(draft) {
  const fields = draft?.fields || {};
  const items = [];

  Object.entries(fields).forEach(([key, value]) => {
    if (!hasValueForRisk(value)) {
      return;
    }

    if (shouldMaskDraftValue(key, value) || getDraftSchema(draft?.entity).fields?.[key]?.sensitive) {
      items.push({
        field: key,
        level: "high",
        message: `${key} 包含敏感凭据，确认来源后再保存。`,
      });
      return;
    }

    if (["disableSignin", "enableExclusiveSignin", "disableSsl"].includes(key) && value === true) {
      items.push({
        field: key,
        level: "high",
        message: `${key} 会影响登录或安全策略，保存前需要二次核对。`,
      });
      return;
    }

    if (["redirectUris", "grantTypes", "homepageUrl", "providerUrl", "customAuthUrl", "customTokenUrl", "customUserInfoUrl"].includes(key)) {
      items.push({
        field: key,
        level: "medium",
        message: `${key} 会影响 OAuth/OIDC 接入链路，请核对环境与域名。`,
      });
    }
  });

  const level = getRiskLevel(items);
  return {
    level,
    label: getDraftRiskLabel(level),
    items,
  };
}

function getOriginFromOptions(options) {
  const fallbackOrigin = typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";
  return String(options?.origin || fallbackOrigin || "").replace(/\/+$/, "");
}

function getFirstArrayValue(value) {
  if (Array.isArray(value)) {
    return value[0] || "";
  }
  return value || "";
}

export function getApplicationAccessArtifacts(draft, options = {}) {
  if (draft?.entity !== "application") {
    return null;
  }

  const fields = draft.fields || {};
  const target = draft.target || {};
  const origin = getOriginFromOptions(options);
  const clientId = fields.clientId || fields.name || target.name || "";
  const redirectUri = getFirstArrayValue(fields.redirectUris);
  const grantTypes = Array.isArray(fields.grantTypes) ? fields.grantTypes : (fields.grantTypes ? [fields.grantTypes] : []);
  const params = new URLSearchParams();
  params.set("client_id", clientId);
  params.set("response_type", "code");
  params.set("redirect_uri", redirectUri);
  params.set("scope", "read");
  params.set("state", "casdoor");

  const authorizeUrl = `${origin}/login/oauth/authorize?${params.toString()}`;
  const tokenUrl = `${origin}/api/login/oauth/access_token`;
  const checklist = [
    clientId ? `在业务系统中配置 Client ID: ${clientId}` : "",
    redirectUri ? `在业务系统中配置 Redirect URI: ${redirectUri}` : "",
    grantTypes.length > 0 ? `在业务系统中配置授权类型: ${grantTypes.join(", ")}` : "",
    `Token Endpoint 使用: ${tokenUrl}`,
  ].filter(Boolean);

  return {
    authorizeUrl,
    tokenUrl,
    testLoginUrl: authorizeUrl,
    checklist,
  };
}

export function buildDraftRevisionMessage(userText, draft) {
  return [
    "请基于上一份 KMAuth AI 草稿进行修订，只修改用户明确要求调整的字段，保留 entity、action 和 target 定位信息。",
    "上一份草稿如下，敏感字段已脱敏；不要把脱敏占位符当作真实凭据写回：",
    getMaskedDraftJson(draft),
    "用户修订要求：",
    String(userText || "").trim(),
    "请返回新的 type=kmauth.applyDraft JSON 草稿，并继续等待用户确认后再反显到页面。",
  ].join("\n");
}

export function readAiDraftHistory() {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }

  try {
    const value = window.localStorage.getItem(DRAFT_HISTORY_STORAGE_KEY);
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function recordAiDraftHistoryEvent(event, draft, extra = {}) {
  if (!draft || typeof window === "undefined" || !window.localStorage) {
    return null;
  }

  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    event,
    entity: draft.entity,
    action: draft.action || "edit",
    targetSummary: getDraftTargetSummary(draft),
    draft: maskDraftValue("", draft),
    extra: maskDraftValue("", extra),
  };
  const history = [...readAiDraftHistory(), entry].slice(-DRAFT_HISTORY_LIMIT);
  window.localStorage.setItem(DRAFT_HISTORY_STORAGE_KEY, JSON.stringify(history));
  return entry;
}

export function getSafeDraftFieldValue(key, value) {
  if (shouldMaskDraftValue(key, value)) {
    return "••••••";
  }

  const text = getDraftFieldValue(maskDraftValue(key, value));
  return text.length > 160 ? `${text.slice(0, 157)}...` : text;
}

function getNestedDraftValue(source, path) {
  return path.split(".").reduce((current, key) => current?.[key], source);
}

function getBaseDraftRequiredPaths(draft) {
  if (draft?.entity === "provider") {
    return draft.action === "add" ? ["fields.owner", "fields.name", "fields.category", "fields.type"] : ["target.owner", "target.name"];
  }
  if (draft?.entity === "application") {
    return draft.action === "add" ? ["fields.owner", "fields.organization", "fields.name"] : ["target.organization", "target.name"];
  }
  if (draft?.entity === "user") {
    return draft.action === "add" ? ["fields.owner", "fields.name"] : ["target.owner", "target.name"];
  }
  if (draft?.entity === "organization") {
    return draft.action === "add" ? ["fields.name"] : ["target.name"];
  }
  return [];
}

function normalizeDraftRequiredPath(path) {
  const value = String(path || "").trim();
  if (!value) {
    return "";
  }
  return value.includes(".") ? value : `fields.${value}`;
}

export function uniqueTexts(values) {
  return [...new Set(values.filter(Boolean))];
}

export function getDraftRequiredPaths(draft) {
  const explicitRequiredMissing = Array.isArray(draft?.requiredMissing) ? draft.requiredMissing.map(normalizeDraftRequiredPath) : [];
  return uniqueTexts([...getBaseDraftRequiredPaths(draft), ...explicitRequiredMissing]);
}

export function getMissingDraftRequiredPaths(draft) {
  return getDraftRequiredPaths(draft).filter((path) => {
    const value = getNestedDraftValue(draft, path);
    return value === null || value === undefined || value === "";
  });
}

export const DRAFT_FIELD_GROUPS = [
  {
    id: "identity",
    title: "定位信息",
    keys: ["owner", "organization", "name", "displayName", "title", "category", "type", "method"],
  },
  {
    id: "endpoint",
    title: "连接端点",
    keys: ["homepageUrl", "redirectUris", "forcedRedirectOrigin", "signupUrl", "signinUrl", "providerUrl", "customAuthUrl", "customTokenUrl", "customUserInfoUrl", "issuerUrl", "endpoint", "domain", "host", "port", "scopes", "emailRegex"],
  },
  {
    id: "credential",
    title: "凭据信息",
    keys: ["clientId", "clientSecret", "clientId2", "clientSecret2", "appId", "accessKey", "secretKey", "privateKey", "keyText", "cert"],
  },
  {
    id: "authorization",
    title: "授权配置",
    keys: ["grantTypes", "tokenFormat", "tokenSigningMethod", "tokenFields", "expireInHours", "refreshExpireInHours", "cookieExpireInHours"],
  },
  {
    id: "template",
    title: "邮件与模板",
    keys: ["title", "content", "metadata", "receiver"],
  },
  {
    id: "advanced",
    title: "高级选项",
    keys: ["enablePkce", "disableSsl", "enableProxy", "enableSignUp", "signName", "customLogo", "httpHeaders", "userMapping"],
  },
];

export function getDraftFieldGroup(key) {
  return DRAFT_FIELD_GROUPS.find((group) => group.keys.includes(key)) || {id: "other", title: "其他字段"};
}

export function getGroupedDraftFields(fields) {
  const groups = new Map();

  Object.entries(fields || {}).forEach(([key, value]) => {
    const group = getDraftFieldGroup(key);
    if (!groups.has(group.id)) {
      groups.set(group.id, {
        title: group.title,
        fields: [],
      });
    }
    groups.get(group.id).fields.push([key, value]);
  });

  return [...groups.values()];
}

function hasSensitiveDraftValue(value, key = "") {
  if (shouldMaskDraftValue(key, value)) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some((item) => hasSensitiveDraftValue(item));
  }

  if (value && typeof value === "object") {
    return Object.entries(value).some(([nestedKey, nestedValue]) => hasSensitiveDraftValue(nestedValue, nestedKey));
  }

  return false;
}

export function getMissingSuggestedProviderFields(draft) {
  if (draft?.entity !== "provider") {
    return [];
  }

  const fields = draft.fields || {};
  const category = fields.category || draft.target?.category || "";
  const type = fields.type || draft.target?.type || "";
  const isMissing = (key) => fields[key] === null || fields[key] === undefined || fields[key] === "";

  if (category === "OAuth" && type === "Custom") {
    return ["clientId", "customAuthUrl", "customTokenUrl", "customUserInfoUrl", "providerUrl", "scopes"].filter(isMissing);
  }

  if (category === "Email") {
    return ["host", "port", "clientId", "clientId2", "clientSecret2", "title"].filter(isMissing);
  }

  return [];
}

export function getMissingSuggestedApplicationFields(draft) {
  if (draft?.entity !== "application") {
    return [];
  }

  const fields = draft.fields || {};
  const isMissing = (key) => fields[key] === null || fields[key] === undefined || fields[key] === "" || (Array.isArray(fields[key]) && fields[key].length === 0);

  return ["displayName", "homepageUrl", "redirectUris", "grantTypes"].filter(isMissing);
}

function isValidHttpUrl(value) {
  if (typeof value !== "string" || value.trim() === "") {
    return true;
  }

  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function isValidProviderPort(value) {
  if (value === null || value === undefined || value === "") {
    return true;
  }

  const port = Number(value);
  return Number.isInteger(port) && port >= 1 && port <= 65535;
}

function hasUrlScheme(value) {
  return typeof value === "string" && /^[a-z][a-z0-9+.-]*:\/\//i.test(value.trim());
}

const APPLICATION_GRANT_TYPES = new Set([
  "authorization_code",
  "password",
  "client_credentials",
  "token",
  "id_token",
  "refresh_token",
  "urn:ietf:params:oauth:grant-type:device_code",
]);

export function getProviderDraftValidationIssues(draft) {
  if (draft?.entity !== "provider") {
    return [];
  }

  const fields = draft.fields || {};
  const issues = [];
  const urlFields = ["providerUrl", "customAuthUrl", "customTokenUrl", "customUserInfoUrl"];

  urlFields.forEach((key) => {
    if (fields[key] !== null && fields[key] !== undefined && fields[key] !== "" && !isValidHttpUrl(fields[key])) {
      issues.push(`fields.${key} 必须是 http:// 或 https:// 开头的 URL。`);
    }
  });

  if (fields.category === "Email" && hasUrlScheme(fields.host)) {
    issues.push("fields.host 应填写 SMTP 主机名或 IP，不要包含 http:// 或 https://。");
  }

  if (!isValidProviderPort(fields.port)) {
    issues.push("fields.port 必须是 1-65535 的端口号。");
  }

  return uniqueTexts(issues);
}

export function getApplicationDraftValidationIssues(draft) {
  if (draft?.entity !== "application") {
    return [];
  }

  const fields = draft.fields || {};
  const issues = [];

  if (fields.redirectUris !== null && fields.redirectUris !== undefined && fields.redirectUris !== "") {
    if (!Array.isArray(fields.redirectUris)) {
      issues.push("fields.redirectUris 必须是 URL 数组。");
    } else {
      fields.redirectUris.forEach((redirectUri, index) => {
        if (!isValidHttpUrl(redirectUri)) {
          issues.push(`fields.redirectUris[${index}] 必须是 http:// 或 https:// 开头的 URL。`);
        }
      });
    }
  }

  ["homepageUrl", "signupUrl", "signinUrl", "forcedRedirectOrigin"].forEach((key) => {
    if (fields[key] !== null && fields[key] !== undefined && fields[key] !== "" && !isValidHttpUrl(fields[key])) {
      issues.push(`fields.${key} 必须是 http:// 或 https:// 开头的 URL。`);
    }
  });

  if (fields.grantTypes !== null && fields.grantTypes !== undefined && fields.grantTypes !== "") {
    if (!Array.isArray(fields.grantTypes)) {
      issues.push("fields.grantTypes 必须是 OAuth/OIDC grant type 数组。");
    } else {
      fields.grantTypes.forEach((grantType, index) => {
        if (!APPLICATION_GRANT_TYPES.has(grantType)) {
          issues.push(`fields.grantTypes[${index}] 不是支持的 OAuth/OIDC grant type。`);
        }
      });
    }
  }

  return uniqueTexts(issues);
}

export function getDraftValidationIssues(draft) {
  if (draft?.entity === "provider") {
    return getProviderDraftValidationIssues(draft);
  }
  if (draft?.entity === "application") {
    return getApplicationDraftValidationIssues(draft);
  }
  return [];
}

export function getDraftReviewWarnings(draft, missingPaths) {
  const warnings = Array.isArray(draft?.warnings) ? draft.warnings : [];
  const suggestedFields = uniqueTexts([
    ...getMissingSuggestedProviderFields(draft),
    ...getMissingSuggestedApplicationFields(draft),
  ]);
  const nextWarnings = [...warnings];

  if (missingPaths.length > 0) {
    nextWarnings.push("补齐必填字段后才能确认反显。");
  }

  if (suggestedFields.length > 0) {
    nextWarnings.push(`建议补充字段：${suggestedFields.join(", ")}。`);
  }

  if (hasSensitiveDraftValue(draft?.fields)) {
    nextWarnings.push("敏感字段已脱敏展示，请确认值来源后再保存。");
  }

  return uniqueTexts(nextWarnings);
}

export function getMaskedDraftJson(draft) {
  return JSON.stringify(maskDraftValue("", draft || {}), null, 2);
}
